import React, { useCallback, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { useMST1060_R10Store } from "./Store_MST1060_R10";
import {
	getMST1060_R10List,
	createMST1060_R10Batch,
	patchMST1060_R10,
	deleteMST1060_R10Batch,
	downloadExcel_MST1060_R10
} from "./Api_MST1060_R10";
import { MST1060_R10Dto, defaultMST1060_R10Dto } from "./Dto_MST1060_R10";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showWarning, showSuccess, ShowSuccessFetch } from "src/utils/toast/toastUtil";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import useUiuxStore from "src/store/useUiuxStore";
import { MST1060_R10Filter } from "./Store_MST1060_R10";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";


interface Props {
	gridRef: React.RefObject<AgGridReact<MST1060_R10Dto> | null>;
	editBarcode: MST1060_R10Dto | null;
	setEditBarcode: (data: MST1060_R10Dto | null) => void;
}

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_MST1060_R10 = (props: Props) => {
	const { setEditBarcode } = props;

	useFetchActionbarAuth("MST1060_R10");

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { setMessage } = useUiuxStore();

	const {
		searchText,
		MST1060_R10Filter,
		barcodeList,
		setBarcodeList,
		setSelectedBarcode,
		selectedBarcodeList,
		deletedBarcodeList,
		setDeletedBarcodeList,
		setOriginalBarcodeList,
		insertBarcodeList,
		addDeletedBarcodeList,
		removeDeletedBarcodeList,
		initSelectedBarcodeList,
		initData,
		setScrollTop,
	} = useMST1060_R10Store();

	const [clickable, setClickable] = useState(true);

	// 엑셀 다운로드
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		try {
			const response = await downloadExcel_MST1060_R10(searchText);
			const base64Data = response.data.data;

			if (base64Data) {
				downloadSettingExcel({
					responseData: base64Data,
					fileName: 'MST1060_R10_바코드관리.xlsx'
				});
				showSuccess('엑셀 다운로드가 완료되었습니다.');
			} else {
				showWarning('다운로드할 데이터가 없습니다.');
			}
		} catch (error) {
			showWarning('엑셀 다운로드 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [searchText, setLoadingSpinner]);


	const getParams = (filter: MST1060_R10Filter) => {
		const params: MST1060_R10Filter = {
			brandCd: filter.brandCd || '',
			itemGroup: filter.itemGroup || '',
			itemAccount: filter.itemAccount || '',
			searchText: filter.searchText || '',
			itemCd: filter.itemCd || '',
			itemNm: filter.itemNm || ''
		};
		return params;
	}

	const defaultMST1060_R10Row = useCallback(() => {
		return {
			...defaultMST1060_R10Dto,
			inserted: true,
		};
	}, []);

	// 바코드 목록 조회
	const fetchData = useCallback(async (options?: FetchOptions) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (messageMode === 'show') {
			setScrollTop(null);
		}

		try {
			const response = await getMST1060_R10List(getParams(MST1060_R10Filter));
			const newList = response.data.data.map((item, index) => ({
				...item,
				inserted: false,
				isModified: false,
				isDeleted: false,
				valueIndex: index,
				orderIndex: index,
				orderIndexSeq: 0
			})) as MST1060_R10Dto[];

			setBarcodeList(newList);
			setOriginalBarcodeList([...newList]);

			initSelectedBarcodeList();
			setDeletedBarcodeList([]);

			if (newList && newList.length > 0) {
				if (messageMode === 'show') {
					ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
				}
			} else {
				if (messageMode === 'show') {
					showWarning('조회된 데이터가 없습니다.');
				}
				setSelectedBarcode(null);
				setEditBarcode(null);
				insertBarcodeList([{ ...defaultMST1060_R10Row(), inserted: true }], 0);
			}
		} catch (error) {
			showWarning('조회 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [setLoadingSpinner, setScrollTop, MST1060_R10Filter, setBarcodeList, setOriginalBarcodeList, initSelectedBarcodeList, setDeletedBarcodeList, setMessage, setSelectedBarcode, setEditBarcode, insertBarcodeList, defaultMST1060_R10Row]);

	// 초기화
	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleInsertClick = useCallback((e: React.MouseEvent, rowIndex?: number) => {
		// rowIndex가 없으면 등록 버튼으로 추가된 행이므로 rowIndex 변수는 undefined 상태로 시작함
		// rowIndex가 있으면 그리드에서 추가된 행이므로 클릭 가능 상태 처리, rowIndex는 그리드에서 추가된 행의 인덱스 값
		const isGridInsert = rowIndex !== undefined;

		InsertRowHandler({
			event: e,
			dataList: barcodeList,
			rowData: isGridInsert ? barcodeList[rowIndex] : { ...defaultMST1060_R10Row(), valueIndex: -1 },
			insertRow: (data: MST1060_R10Dto[], afterValueIndex: number) => {
				const arrayIndex = barcodeList.findIndex(b => b.valueIndex === afterValueIndex);
				insertBarcodeList(data, arrayIndex);
			},
			defaultRowData: defaultMST1060_R10Row(),
			...(isGridInsert && { setClickable }),
		});
	}, [barcodeList, insertBarcodeList, defaultMST1060_R10Row, setClickable]);

	// 삭제 (삭제 표시 토글)
	const handleDeleteClick = useCallback(() => {
		//console.log('=== 삭제 버튼 클릭 ===');
		//console.log('selectedBarcodeList:', selectedBarcodeList);
		//console.log('deletedBarcodeList:', deletedBarcodeList);

		if (deletedBarcodeList.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			//console.log('→ 삭제 복구 실행');
			removeDeletedBarcodeList(deletedBarcodeList);
			initSelectedBarcodeList();
		} else if (selectedBarcodeList.length > 0) {
			// 선택된 행만 삭제 표시
			//console.log('→ 삭제 표시 실행:', selectedBarcodeList);
			addDeletedBarcodeList(selectedBarcodeList);
			initSelectedBarcodeList();
		} else {
			//console.log('→ 선택된 행 없음');
			showWarning('삭제할 바코드를 선택해주세요.');
		}
	}, [selectedBarcodeList, deletedBarcodeList, addDeletedBarcodeList, removeDeletedBarcodeList, initSelectedBarcodeList]);

	// 저장 데이터 검증 함수
	const validateSaveData = (
		insertedList: MST1060_R10Dto[], 
		modifiedList: MST1060_R10Dto[], 
		deletedList: MST1060_R10Dto[],
		activeList: MST1060_R10Dto[]
	): boolean => {
		// 1. 저장할 데이터가 있는지 확인
		if (insertedList.length === 0 && modifiedList.length === 0 && deletedList.length === 0) {
			showWarning('저장할 데이터가 없습니다.');
			return false;
		}

		const targetList = [...insertedList, ...modifiedList];

		// 2. 필수값 검증
		for (const item of targetList) {
			if (!item.barcode) {
				showWarning('바코드를 입력해주세요.');
				return false;
			}
			if (!item.itemCd) {
				showWarning('품목코드를 입력해주세요.');
				return false;
			}
			if (!item.packQty || item.packQty <= 0) {
				showWarning('포장수량을 입력해주세요.');
				return false;
			}

		}

		// 3. 바코드 중복 검증
		const barcodes = activeList.map(item => item.barcode?.trim()).filter(Boolean);
		const duplicates = barcodes.filter((barcode, index) => barcodes.indexOf(barcode) !== index);
		if (duplicates.length > 0) {
			showWarning(`중복된 바코드가 있습니다: ${duplicates[0]}`);
			return false;
		}

		return true;
	};

	// 저장
	const handleSaveClick = useCallback(async () => {
		// 신규/수정/삭제 데이터 분리
		const isDeleted = (item: MST1060_R10Dto) =>
			deletedBarcodeList.some(del => String(del) === String(item.valueIndex));
		const activeList = barcodeList.filter(item => !isDeleted(item));
		const insertedList = activeList.filter(item => item.inserted);
		const modifiedList = activeList.filter(item => item.isModified && !item.inserted);
		const deletedList = barcodeList.filter(item => !item.inserted && isDeleted(item));

		// 데이터 검증
		if (!validateSaveData(insertedList, modifiedList, deletedList, activeList)) {
			return;
		}

		setLoadingSpinner(true);

		try {
			// 신규 등록
			if (insertedList.length > 0) {
				const createData: MST1060_R10Dto[] = insertedList.map(item => ({
					...defaultMST1060_R10Dto,
					barcode: item.barcode,
					itemCd: item.itemCd,
					packQty: item.packQty,
					itemUnit: item.itemUnit,
					remark: item.remark,
					inserted: item.inserted ?? true,
					isModified: item.isModified ?? false,
					isDeleted: item.isDeleted ?? false,
				}));
				await createMST1060_R10Batch(createData);
			}

			// 수정
			for (const item of modifiedList) {
				const updateData: MST1060_R10Dto = {
					...defaultMST1060_R10Dto,
					barcode: item.barcode,
					itemCd: item.itemCd,
					packQty: item.packQty,
					itemUnit: item.itemUnit,
					remark: item.remark,
					inserted: item.inserted ?? false,
					isModified: item.isModified ?? true,
					isDeleted: item.isDeleted ?? false,
				};
				await patchMST1060_R10(updateData);
			}

			// 삭제
			if (deletedList.length > 0) {
				const deleteBarcodes = deletedList.map(item => item.barcode);
				await deleteMST1060_R10Batch(deleteBarcodes);
			}

			showSuccess('저장이 완료되었습니다.');

			// 목록 새로고침
			await fetchData({ messageMode: 'none' });
		} catch (error: any) {
			const errorMessage = error?.response?.data?.message || '저장 중 오류가 발생했습니다.';
			showWarning(errorMessage);
		} finally {
			setLoadingSpinner(false);
		}
	}, [barcodeList, deletedBarcodeList, setLoadingSpinner, fetchData]);

	return {
		clickable, setClickable,
		handleExcelClick,
		fetchData,
		defaultMST1060_R10Row,
		initContainerData,
		handleInsertClick,
		handleDeleteClick,
		handleSaveClick,
	};
};

